import React, { useState } from 'react';
import { 
  Button, 
  Input, 
  Textarea, 
  Checkbox, 
  RadioGroup,
  Switch,
  Select,
  Badge, 
  Avatar, 
  AvatarGroup,
  Alert,
  Progress,
  CircularProgress,
  Spinner,
  Container,
  Stack,
  VStack,
  HStack,
  Grid,
  GridItem,
  Modal,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SimpleTooltip,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Breadcrumb,
  BreadcrumbWithIcons,
  Pagination,
  SimplePagination,
  HorizontalBarChart,
  QuickActions,
  ListingBlock,
  Steps,
  createSteps,
  updateStepStatus,
  LocationAutocomplete,
  StatCard,
  AIIcon,
  TableActionButton,
  TableActions,
} from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, CardExpandable } from '@/components/ui/Card/Card';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { MonthView, WeekView, DayView, AgendaView, Calendar, type CalendarEvent } from '@/components/ui/Calendar';
import { Table } from '@/components/ui/Table';
import { useNotificationStore } from '@/stores/notificationStore';
import { Calendar as CalendarIcon, Users, FileText, DollarSign, Settings, CheckCircle, FilePen, Archive, Trash2, Plus } from 'lucide-react';

export function ComponentDemo() {
  const [inputValue, setInputValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [radioValue, setRadioValue] = useState('option1');
  const [switchValue, setSwitchValue] = useState(false);
  const [selectValue, setSelectValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progressValue, setProgressValue] = useState(65);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const [cardExpanded, setCardExpanded] = useState(false);
  
  const notifySuccess = useNotificationStore(s => s.success);
  const demoEvents: CalendarEvent[] = [
    { id: '1', title: 'Instagram Carousel: Workshop Teaser', start: new Date(), end: new Date(), networks: ['instagram'], status: 'scheduled', colorVariant: 'primary' },
    { id: '2', title: 'Blog Post Share: Managing Anxiety', start: new Date(new Date().setDate(new Date().getDate() + 1)), end: new Date(new Date().setDate(new Date().getDate() + 1)), networks: ['facebook','linkedin'], status: 'draft', colorVariant: 'accent' },
    { id: '3', title: 'X Thread: Tips for Parents', start: new Date(new Date().setDate(new Date().getDate() + 2)), end: new Date(new Date().setDate(new Date().getDate() + 2)), networks: ['x'], status: 'scheduled', colorVariant: 'secondary' },
  ];
  
  return (
    <div className="bg-background px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-12">
        <div>
          <h1 className="text-4xl font-bold text-foreground mb-2">UI Component Library</h1>
          <p className="text-lg text-muted-foreground">
            A comprehensive collection of accessible, responsive components built with our design system.
          </p>
        </div>

        {/* Code Example Component */}
        <div className="mb-8 p-4 rounded-lg" style={{ backgroundColor: '#efede7' }}>
          <h3 className="text-sm font-semibold mb-2">💡 Tip: Code Examples</h3>
          <p className="text-sm text-muted-foreground">
            Each component below includes example code. Look for the code blocks to see how to use each component.
          </p>
        </div>

        {/* Color Palette */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Color Palette</h2>
          <div className="space-y-8">
            {/* Primary Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Primary Colors</h3>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                <div className="text-center">
                  <div className="bg-primary-50 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">50</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-100 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">100</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-200 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">200</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-300 h-16 rounded-lg mb-1" />
                  <span className="text-xs">300</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-400 h-16 rounded-lg mb-1" />
                  <span className="text-xs">400</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-500 h-16 rounded-lg mb-1" />
                  <span className="text-xs">500</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-600 h-16 rounded-lg mb-1" />
                  <span className="text-xs">600</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-700 h-16 rounded-lg mb-1" />
                  <span className="text-xs">700</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-800 h-16 rounded-lg mb-1" />
                  <span className="text-xs">800</span>
                </div>
                <div className="text-center">
                  <div className="bg-primary-900 h-16 rounded-lg mb-1" />
                  <span className="text-xs">900</span>
                </div>
              </div>
            </div>

            {/* Gray Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Gray Colors</h3>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                <div className="text-center">
                  <div className="bg-gray-50 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">50</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-100 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">100</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-200 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">200</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-300 h-16 rounded-lg mb-1" />
                  <span className="text-xs">300</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-400 h-16 rounded-lg mb-1" />
                  <span className="text-xs">400</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-500 h-16 rounded-lg mb-1" />
                  <span className="text-xs">500</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-600 h-16 rounded-lg mb-1" />
                  <span className="text-xs">600</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-700 h-16 rounded-lg mb-1" />
                  <span className="text-xs">700</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-800 h-16 rounded-lg mb-1" />
                  <span className="text-xs">800</span>
                </div>
                <div className="text-center">
                  <div className="bg-gray-900 h-16 rounded-lg mb-1" />
                  <span className="text-xs">900</span>
                </div>
              </div>
            </div>

            {/* Secondary Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Secondary Colors (Teal)</h3>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                <div className="text-center">
                  <div className="bg-secondary-50 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">50</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-100 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">100</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-200 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">200</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-300 h-16 rounded-lg mb-1" />
                  <span className="text-xs">300</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-400 h-16 rounded-lg mb-1" />
                  <span className="text-xs">400</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-500 h-16 rounded-lg mb-1" />
                  <span className="text-xs">500</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-600 h-16 rounded-lg mb-1" />
                  <span className="text-xs">600</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-700 h-16 rounded-lg mb-1" />
                  <span className="text-xs">700</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-800 h-16 rounded-lg mb-1" />
                  <span className="text-xs">800</span>
                </div>
                <div className="text-center">
                  <div className="bg-secondary-900 h-16 rounded-lg mb-1" />
                  <span className="text-xs">900</span>
                </div>
              </div>
            </div>

            {/* Accent Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Accent Colors (Amber)</h3>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                <div className="text-center">
                  <div className="bg-accent-50 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">50</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-100 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">100</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-200 border h-16 rounded-lg mb-1" />
                  <span className="text-xs">200</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-300 h-16 rounded-lg mb-1" />
                  <span className="text-xs">300</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-400 h-16 rounded-lg mb-1" />
                  <span className="text-xs">400</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-500 h-16 rounded-lg mb-1" />
                  <span className="text-xs">500</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-600 h-16 rounded-lg mb-1" />
                  <span className="text-xs">600</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-700 h-16 rounded-lg mb-1" />
                  <span className="text-xs">700</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-800 h-16 rounded-lg mb-1" />
                  <span className="text-xs">800</span>
                </div>
                <div className="text-center">
                  <div className="bg-accent-900 h-16 rounded-lg mb-1" />
                  <span className="text-xs">900</span>
                </div>
              </div>
            </div>

            {/* Color Usage */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Color Usage Guidelines</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border bg-primary-50 border-primary-200">
                  <h4 className="font-medium text-primary-900 mb-2">Primary Actions</h4>
                  <p className="text-sm text-primary-700">Use primary colors for main CTAs and important actions.</p>
                </div>
                <div className="p-4 rounded-lg border bg-secondary-50 border-secondary-200">
                  <h4 className="font-medium text-secondary-900 mb-2">Secondary Elements</h4>
                  <p className="text-sm text-secondary-700">Use secondary colors for supportive elements and accents.</p>
                </div>
                <div className="p-4 rounded-lg border bg-accent-50 border-accent-200">
                  <h4 className="font-medium text-accent-900 mb-2">Attention & Highlights</h4>
                  <p className="text-sm text-accent-700">Use accent colors to draw attention to specific elements.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Buttons</h2>
          <div className="space-y-8">
            {/* Variants */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Variants</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="accent">Accent</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="dashed" leftIcon={<Plus className="w-4 h-4" />}>Dashed</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="link">Link</Button>
                <Button variant="danger">Danger</Button>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Button variant="primary">Primary</Button>
<Button variant="secondary">Secondary</Button>
<Button variant="accent">Accent</Button>
<Button variant="outline">Outline</Button>
<Button variant="dashed" leftIcon={<Plus />}>Dashed</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="link">Link</Button>
<Button variant="danger">Danger</Button>`}
              </pre>
            </div>

            {/* Dashed Border Special Use Case */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Dashed Border Button</h3>
              <p className="text-sm text-gray-600 mb-4">
                The dashed variant is ideal for "add" actions or placeholder buttons that invite user interaction.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="dashed" leftIcon={<Plus className="w-4 h-4" />}>Add Item</Button>
                <Button variant="dashed" leftIcon={<Plus className="w-4 h-4" />} size="sm">Add Tag</Button>
                <Button variant="dashed" leftIcon={<Plus className="w-4 h-4" />} size="lg">Add Destination</Button>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Common use cases for dashed buttons:
<Button variant="dashed" leftIcon={<Plus />}>Add Item</Button>
<Button variant="dashed" leftIcon={<Plus />} size="sm">Add Tag</Button>

// Works well alongside tabs or chip-style buttons
<Button variant="outline">Paris</Button>
<Button variant="outline">London</Button>
<Button variant="dashed" leftIcon={<Plus />}>Add Destination</Button>`}
              </pre>
            </div>

            {/* Sizes */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Sizes</h3>
              <div className="flex items-center gap-3">
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
                <Button size="icon">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </Button>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Button size="sm">Small</Button>
<Button size="md">Medium</Button>
<Button size="lg">Large</Button>
<Button size="icon">
  <PlusIcon className="w-4 h-4" />
</Button>`}
              </pre>
            </div>

            {/* States */}
            <div>
              <h3 className="text-lg font-semibold mb-4">States</h3>
              <div className="flex gap-3">
                <Button disabled>Disabled</Button>
                <Button loading>Loading</Button>
                <Button variant="primary" icon={<span className="text-lg">🚀</span>}>
                  With Icon
                </Button>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Button disabled>Disabled</Button>
<Button loading>Loading</Button>
<Button variant="primary" icon={<RocketIcon />}>
  With Icon
</Button>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Table Action Buttons */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Table Action Buttons</h2>
          <div className="space-y-8">
            {/* Variants */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Variants</h3>
              <div className="flex flex-wrap gap-3">
                <TableActions>
                  <TableActionButton icon={FilePen}>Edit</TableActionButton>
                  <TableActionButton variant="destructive" icon={Archive}>Archive</TableActionButton>
                </TableActions>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { TableActionButton, TableActions } from '@/components/ui';
import { FilePen, Archive } from 'lucide-react';

<TableActions>
  <TableActionButton icon={FilePen}>Edit</TableActionButton>
  <TableActionButton variant="destructive" icon={Archive}>Archive</TableActionButton>
</TableActions>`}
              </pre>
            </div>

            {/* With Different Icons */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Common Actions</h3>
              <div className="flex flex-wrap gap-3">
                <TableActions>
                  <TableActionButton icon={FilePen}>Edit</TableActionButton>
                  <TableActionButton variant="destructive" icon={Trash2}>Delete</TableActionButton>
                </TableActions>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<TableActions>
  <TableActionButton icon={FilePen}>Edit</TableActionButton>
  <TableActionButton variant="destructive" icon={Trash2}>Delete</TableActionButton>
</TableActions>`}
              </pre>
            </div>

            {/* Usage in Tables */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Usage in Table Columns</h3>
              <p className="text-sm text-gray-600 mb-4">
                TableActionButton should be used within TableActions wrapper for consistent spacing and alignment in table action columns.
              </p>
              <pre className="p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`columns={[
  { key: 'name', title: 'Name' },
  { key: 'email', title: 'Email' },
  {
    key: 'actions',
    title: 'Actions',
    align: 'right',
    render: (_, record) => (
      <TableActions>
        <TableActionButton icon={FilePen} onClick={() => handleEdit(record)}>
          Edit
        </TableActionButton>
        <TableActionButton 
          variant="destructive" 
          icon={Archive} 
          onClick={() => handleArchive(record)}
        >
          Archive
        </TableActionButton>
      </TableActions>
    )
  }
]}`}
              </pre>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Form Elements</h2>
          <div className="grid gap-8 md:grid-cols-2">
            {/* Text Inputs */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Text Inputs</h3>
              <Stack spacing={4}>
                <Input
                  label="Name"
                  placeholder="Enter your name"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="john@example.com"
                  hint="We'll never share your email"
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  error="Password must be at least 8 characters"
                />
                <Input
                  label="With Icon"
                  icon="👤"
                  placeholder="Username"
                />
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Input
  label="Name"
  placeholder="Enter your name"
  value={value}
  onChange={(e) => setValue(e.target.value)}
/>
<Input
  label="Email"
  type="email"
  placeholder="john@example.com"
  hint="We'll never share your email"
/>
<Input
  label="Password"
  type="password"
  error="Password must be at least 8 characters"
/>`}
              </pre>
            </div>

            {/* Textarea */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Textarea</h3>
              <Stack spacing={4}>
                <Textarea
                  label="Message"
                  placeholder="Enter your message..."
                  value={textareaValue}
                  onChange={(e) => setTextareaValue(e.target.value)}
                />
                <Textarea
                  label="Description"
                  placeholder="Describe your project..."
                  hint="Maximum 500 characters"
                  rows={4}
                />
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Textarea
  label="Message"
  placeholder="Enter your message..."
  value={value}
  onChange={(e) => setValue(e.target.value)}
/>
<Textarea
  label="Description"
  placeholder="Describe your project..."
  hint="Maximum 500 characters"
  rows={4}
/>`}
              </pre>
            </div>

            {/* Select */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Select</h3>
              <Stack spacing={4}>
                <Select
                  label="Country"
                  value={selectValue}
                  onChange={(e) => setSelectValue(e.target.value)}
                  placeholder="Select a country"
                >
                  <option value="">Select a country</option>
                  <option value="us">United States</option>
                  <option value="uk">United Kingdom</option>
                  <option value="ca">Canada</option>
                  <option value="au">Australia</option>
                </Select>
                <Select
                  label="Language"
                  hint="Choose your preferred language"
                  defaultValue=""
                >
                  <option value="">Select language</option>
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </Select>
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Select
  label="Country"
  value={value}
  onChange={(e) => setValue(e.target.value)}
  placeholder="Select a country"
>
  <option value="">Select a country</option>
  <option value="us">United States</option>
  <option value="uk">United Kingdom</option>
  <option value="ca">Canada</option>
</Select>`}
              </pre>
            </div>

            {/* Checkboxes & Radio */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Checkboxes & Radio Buttons</h3>
              <Stack spacing={4}>
                <Checkbox
                  checked={checkboxValue}
                  onChange={(e) => setCheckboxValue(e.target.checked)}
                  label="I agree to the terms and conditions"
                />
                <Checkbox
                  label="Subscribe to newsletter"
                  hint="Get weekly updates"
                />
                <RadioGroup
                  value={radioValue}
                  onChange={setRadioValue}
                  label="Choose your plan"
                  options={[
                    { value: 'free', label: 'Free Plan' },
                    { value: 'pro', label: 'Pro Plan ($9/month)' },
                    { value: 'enterprise', label: 'Enterprise', disabled: true },
                  ]}
                />
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Checkbox
  checked={checked}
  onChange={(e) => setChecked(e.target.checked)}
  label="I agree to the terms"
/>

<RadioGroup
  value={value}
  onChange={setValue}
  label="Choose your plan"
  options={[
    { value: 'free', label: 'Free Plan' },
    { value: 'pro', label: 'Pro Plan' },
    { value: 'enterprise', label: 'Enterprise' },
  ]}
/>`}
              </pre>
            </div>

            {/* Switch */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Switch</h3>
              <Stack spacing={4}>
                <Switch
                  checked={switchValue}
                  onChange={(e) => setSwitchValue(e.target.checked)}
                  label="Enable notifications"
                />
                <Switch
                  label="Dark mode"
                  labelPosition="left"
                />
                <Switch
                  label="Maintenance mode"
                  disabled
                />
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Switch
  checked={checked}
  onChange={(e) => setChecked(e.target.checked)}
  label="Enable notifications"
/>
<Switch
  label="Dark mode"
  labelPosition="left"
/>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Display Components */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Display Components</h2>
          <div className="space-y-8">
            {/* Charts */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Charts</h3>
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <div className="p-4">
                    <CardTitle className="text-base">Horizontal Bar Chart</CardTitle>
                    <CardDescription>Use for simple ranked comparisons</CardDescription>
                  </div>
                  <CardContent className="px-4 pb-4">
                    <HorizontalBarChart
                      unit="ms"
                      data={[
                        { label: 'embed_doc (42)', value: 820 },
                        { label: 'gallery_image_analysis (11)', value: 460 },
                        { label: 'org_profile_research (7)', value: 210 },
                      ]}
                    />
                  </CardContent>
                </Card>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">{`import { HorizontalBarChart } from '@/components/ui';

<HorizontalBarChart
  unit="ms"
  data={[
    { label: 'embed_doc (42)', value: 820 },
    { label: 'gallery_image_analysis (11)', value: 460 },
    { label: 'org_profile_research (7)', value: 210 },
  ]}
/>`}
              </pre>
            </div>
            {/* Badges */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Badges</h3>
              <div className="flex flex-wrap gap-2">
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="error">Error</Badge>
                <Badge variant="outline">Outline</Badge>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Badge>Default</Badge>
<Badge variant="secondary">Secondary</Badge>
<Badge variant="success">Success</Badge>
<Badge variant="warning">Warning</Badge>
<Badge variant="error">Error</Badge>
<Badge variant="outline">Outline</Badge>`}
              </pre>
            </div>

            {/* AI Icon */}
            <div>
              <h3 className="text-lg font-semibold mb-4">AI Icon</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Standardized icon for AI-powered features across the platform
              </p>
              <div className="space-y-4">
                {/* Icon Variants */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold mb-3">Variants</h4>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <AIIcon className="w-8 h-8 mb-2" />
                      <span className="text-xs text-gray-600">Default</span>
                    </div>
                    <div className="text-center">
                      <AIIcon variant="primary" className="w-8 h-8 mb-2" />
                      <span className="text-xs text-gray-600">Primary</span>
                    </div>
                    <div className="text-center">
                      <AIIcon variant="muted" className="w-8 h-8 mb-2" />
                      <span className="text-xs text-gray-600">Muted</span>
                    </div>
                  </div>
                </div>

                {/* Icon in Buttons */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold mb-3">In Buttons</h4>
                  <div className="flex flex-wrap gap-3">
                    <Button size="sm">
                      <AIIcon className="w-4 h-4 mr-2" />
                      Run Research
                    </Button>
                    <Button variant="outline" size="sm">
                      <AIIcon className="w-4 h-4 mr-2" />
                      AI Assist
                    </Button>
                    <Button variant="ghost" size="sm" className="hover:bg-accent-50">
                      <AIIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Different Sizes */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold mb-3">Sizes</h4>
                  <div className="flex items-end gap-4">
                    <AIIcon className="w-4 h-4" />
                    <AIIcon className="w-5 h-5" />
                    <AIIcon className="w-6 h-6" />
                    <AIIcon className="w-8 h-8" />
                    <AIIcon className="w-10 h-10" />
                  </div>
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Default variant
<AIIcon className="w-4 h-4" />

// Primary variant (active/prominent)
<AIIcon variant="primary" className="w-4 h-4" />

// Muted variant (disabled/secondary)
<AIIcon variant="muted" className="w-4 h-4" />

// In a button
<Button>
  <AIIcon className="w-4 h-4 mr-2" />
  AI Research
</Button>

// Ghost button with hover effect
<Button variant="ghost" className="hover:bg-accent-50">
  <AIIcon className="w-4 h-4" />
</Button>`}
              </pre>
            </div>

            {/* Avatars */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Avatars</h3>
              <Stack spacing={4}>
                <div className="flex items-center gap-3">
                  <Avatar size="sm" initials="JD" name="John Doe" />
                  <Avatar size="md" initials="AB" name="Alice Brown" />
                  <Avatar size="lg" initials="CS" name="Charlie Smith" />
                  <Avatar size="xl" src="https://i.pravatar.cc/150?img=1" name="Sarah Wilson" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Avatar Group</p>
                  <AvatarGroup max={3}>
                    <Avatar initials="JD" name="John Doe" />
                    <Avatar initials="AB" name="Alice Brown" />
                    <Avatar initials="CS" name="Charlie Smith" />
                    <Avatar initials="MJ" name="Mary Johnson" />
                    <Avatar initials="RW" name="Robert Wilson" />
                  </AvatarGroup>
                </div>
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Avatar size="md" initials="JD" name="John Doe" />
<Avatar size="lg" src="/avatar.jpg" name="Sarah" />

<AvatarGroup max={3}>
  <Avatar initials="JD" name="John Doe" />
  <Avatar initials="AB" name="Alice Brown" />
  <Avatar initials="CS" name="Charlie Smith" />
  <Avatar initials="MJ" name="Mary Johnson" />
</AvatarGroup>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Stat Cards */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Stat Cards</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Stat Cards</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  label="Total Users"
                  value="2,847"
                  icon={<Users className="w-5 h-5" />}
                />
                <StatCard
                  label="Revenue"
                  value="$24,890"
                  valueColor="success"
                  iconColor="success"
                  icon={<DollarSign className="w-5 h-5" />}
                  trend="+12% from last month"
                />
                <StatCard
                  label="Active Sessions"
                  value="1,294"
                  valueColor="warning"
                  iconColor="warning"
                  icon={<Users className="w-5 h-5" />}
                  trend="+5% from yesterday"
                />
                <StatCard
                  label="Error Rate"
                  value="0.8%"
                  valueColor="error"
                  iconColor="error"
                  icon={<FileText className="w-5 h-5" />}
                  trend="-2% from last week"
                />
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<StatCard
  label="Total Users"
  value="2,847"
  icon={<Users className="w-5 h-5" />}
/>

<StatCard
  label="Revenue"
  value="$24,890"
  valueColor="success"
  iconColor="success"
  icon={<DollarSign className="w-5 h-5" />}
  trend="+12% from last month"
/>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Interactive & Loading States</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  label="Clickable Metric"
                  value="450"
                  icon={<Settings className="w-5 h-5" />}
                  onClick={() => notifySuccess('StatCard clicked!')}
                  trend="Click me!"
                />
                <StatCard
                  label="Loading State"
                  value="Loading..."
                  loading={true}
                  icon={<Users className="w-5 h-5" />}
                />
                <StatCard
                  label="Custom Content"
                  value={<Badge variant="success">Active</Badge>}
                  icon={<CheckCircle className="w-5 h-5" />}
                  iconColor="success"
                />
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Interactive card
<StatCard
  label="Clickable Metric"
  value="450"
  icon={<Settings className="w-5 h-5" />}
  onClick={() => handleClick()}
  trend="Click me!"
/>

// Loading state
<StatCard
  label="Loading State"
  value="Loading..."
  loading={true}
  icon={<Users className="w-5 h-5" />}
/>

// Custom value content
<StatCard
  label="Custom Content"
  value={<Badge variant="success">Active</Badge>}
  icon={<CheckCircle className="w-5 h-5" />}
  iconColor="success"
/>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Color Variants</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <StatCard
                  label="Default"
                  value="100"
                  icon={<Users className="w-5 h-5" />}
                  iconColor="default"
                />
                <StatCard
                  label="Primary"
                  value="200"
                  icon={<Users className="w-5 h-5" />}
                  iconColor="primary"
                />
                <StatCard
                  label="Success"
                  value="300"
                  valueColor="success"
                  icon={<Users className="w-5 h-5" />}
                  iconColor="success"
                />
                <StatCard
                  label="Warning"
                  value="400"
                  valueColor="warning"
                  icon={<Users className="w-5 h-5" />}
                  iconColor="warning"
                />
                <StatCard
                  label="Error"
                  value="500"
                  valueColor="error"
                  icon={<Users className="w-5 h-5" />}
                  iconColor="error"
                />
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Available color variants:
iconColor: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'muted'
valueColor: 'default' | 'success' | 'warning' | 'error' | 'muted'

<StatCard
  label="Success Metric"
  value="95.2%"
  valueColor="success"
  iconColor="success"
  icon={<CheckCircle className="w-5 h-5" />}
/>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Card Components */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Card Components</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Cards</h3>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
                {/* Default Card */}
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Default Card</CardTitle>
                    <CardDescription>Standard card with border</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      This is the default card variant with a border and white background. 
                      Notice the improved padding throughout the card.
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button size="sm">Action</Button>
                  </CardFooter>
                </Card>

                {/* Elevated Card */}
                <Card variant="elevated" className="h-full">
                  <CardHeader>
                    <CardTitle>Elevated Card</CardTitle>
                    <CardDescription>Card with shadow instead of border</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      This card uses a shadow for elevation without a border.
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button size="sm" variant="secondary">Action</Button>
                  </CardFooter>
                </Card>

                {/* Bordered Card */}
                <Card variant="bordered" className="h-full">
                  <CardHeader>
                    <CardTitle>Bordered Card</CardTitle>
                    <CardDescription>Transparent background with border</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      This card has a transparent background with just a border.
                    </p>
                  </CardContent>
                  <CardFooter className="justify-end gap-2">
                    <Button size="sm" variant="ghost">Cancel</Button>
                    <Button size="sm" variant="outline">Save</Button>
                  </CardFooter>
                </Card>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Card>
  <CardHeader>
    <CardTitle>Card Title</CardTitle>
    <CardDescription>Card description</CardDescription>
  </CardHeader>
  <CardContent>
    Content goes here
  </CardContent>
  <CardFooter>
    <Button>Action</Button>
  </CardFooter>
</Card>

<Card variant="elevated">
  <CardHeader>
    <CardTitle>Elevated Card</CardTitle>
  </CardHeader>
  <CardContent>
    Shadow instead of border
  </CardContent>
</Card>

<Card variant="bordered">
  <CardHeader>
    <CardTitle>Bordered Card</CardTitle>
  </CardHeader>
  <CardContent>
    Transparent with border
  </CardContent>
</Card>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Interactive Cards</h3>
              <div className="grid gap-6 md:grid-cols-2">
                <Card interactive onClick={() => alert('Card clicked!')}>
                  <CardHeader>
                    <CardTitle>Interactive Card</CardTitle>
                    <CardDescription>Click me!</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      This card has hover effects and is clickable.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader onClick={() => setCardExpanded(!cardExpanded)} className="cursor-pointer">
                    <div className="flex-1">
                      <CardTitle>Expandable Card</CardTitle>
                      <CardDescription>Click header to expand</CardDescription>
                    </div>
                    <svg 
                      className={`w-5 h-5 transition-transform ml-4 ${cardExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </CardHeader>
                  <CardExpandable isExpanded={cardExpanded}>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        This content is revealed with a smooth animation when the header is clicked.
                      </p>
                      <div className="mt-3 space-y-2">
                        <div className="h-2 bg-gray-200 rounded"></div>
                        <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </CardContent>
                  </CardExpandable>
                </Card>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Card interactive onClick={() => alert('Clicked!')}>
  <CardHeader>
    <CardTitle>Interactive Card</CardTitle>
  </CardHeader>
  <CardContent>
    Clickable with hover effects
  </CardContent>
</Card>

const [expanded, setExpanded] = useState(false);

<Card>
  <CardHeader onClick={() => setExpanded(!expanded)}>
    <div className="flex-1">
      <CardTitle>Expandable Card</CardTitle>
      <CardDescription>Click to expand</CardDescription>
    </div>
    <ChevronIcon className={\`ml-4 \${expanded ? 'rotate-180' : ''}\`} />
  </CardHeader>
  <CardExpandable isExpanded={expanded}>
    <CardContent>
      Hidden content with animation
    </CardContent>
  </CardExpandable>
</Card>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Card with Header Actions</h3>
              <div className="max-w-2xl">
                <Card>
                  <CardHeader>
                    <div className="flex-1">
                      <CardTitle>User Settings</CardTitle>
                      <CardDescription>Manage user preferences and configurations</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button size="sm" variant="ghost">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </Button>
                      <Button size="sm" variant="outline">Edit</Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-1">Notification Settings</p>
                      <p className="text-sm text-muted-foreground">Email notifications are enabled</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-1">Privacy</p>
                      <p className="text-sm text-muted-foreground">Profile is visible to team members</p>
                    </div>
                  </CardContent>
                  <CardFooter className="justify-between">
                    <p className="text-xs text-muted-foreground">Last updated 2 hours ago</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost">Reset</Button>
                      <Button size="sm">Save Changes</Button>
                    </div>
                  </CardFooter>
                </Card>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Card>
  <CardHeader>
    <div className="flex-1">
      <CardTitle>User Settings</CardTitle>
      <CardDescription>Manage user preferences</CardDescription>
    </div>
    <div className="flex items-center gap-2 ml-4">
      <Button size="sm" variant="ghost">
        <RefreshIcon />
      </Button>
      <Button size="sm" variant="outline">Edit</Button>
    </div>
  </CardHeader>
  <CardContent>
    Content goes here
  </CardContent>
  <CardFooter className="justify-between">
    <p className="text-xs text-muted-foreground">Last updated 2 hours ago</p>
    <div className="flex gap-2">
      <Button size="sm" variant="ghost">Reset</Button>
      <Button size="sm">Save Changes</Button>
    </div>
  </CardFooter>
</Card>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Card Padding Options</h3>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Card padding="none">
                  <div className="p-4 border-b bg-gray-50">
                    <CardTitle>No Padding</CardTitle>
                  </div>
                  <div className="p-4">
                    <p className="text-sm">Custom padding control</p>
                  </div>
                </Card>

                <Card padding="sm">
                  <CardHeader>
                    <CardTitle>Small</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">16px padding</p>
                  </CardContent>
                </Card>

                <Card padding="md">
                  <CardHeader>
                    <CardTitle>Medium</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">24px padding (default)</p>
                  </CardContent>
                </Card>

                <Card padding="lg">
                  <CardHeader>
                    <CardTitle>Large</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">32px padding</p>
                  </CardContent>
                </Card>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Card padding="none">     // No padding
<Card padding="sm">       // 16px padding  
<Card padding="md">       // 24px padding (default)
<Card padding="lg">       // 32px padding`}
              </pre>
            </div>
          </div>
        </section>

        {/* Listing Block Component */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Listing Blocks</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Standard Listing Blocks</h3>
              <div className="space-y-6">
                {/* Client-style block with meta items */}
                <div className="border rounded-lg p-6 bg-white">
                  <h4 className="text-sm font-medium mb-3">Client Listing Style</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ListingBlock
                      title="Acme Corporation"
                      subtitle="John Smith"
                      metaItems={[
                        {
                          icon: <span className="w-4 h-4 text-blue-500">📧</span>,
                          label: 'Email',
                          value: 'john@acme.com'
                        },
                        {
                          icon: <span className="w-4 h-4 text-green-500">📞</span>,
                          label: 'Phone',
                          value: '+1 (555) 123-4567'
                        }
                      ]}
                      badge={{
                        label: 'ID: acme-corp',
                        variant: 'secondary'
                      }}
                      actions={[
                        { label: 'Edit', onClick: () => console.log('Edit client') },
                        { label: 'Delete', onClick: () => console.log('Delete client') }
                      ]}
                      onClick={() => console.log('Navigate to client')}
                    />
                  </div>
                </div>

                {/* Event-style block with description */}
                <div className="border rounded-lg p-6 bg-white">
                  <h4 className="text-sm font-medium mb-3">Event Listing Style</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ListingBlock
                      title="Annual Conference 2024"
                      subtitle="/annual-conference-2024"
                      description="Our biggest event of the year featuring keynote speakers, workshops, and networking opportunities for industry professionals."
                      badge={{
                        label: 'ID: conf-2024',
                        variant: 'secondary'
                      }}
                      actions={[
                        { label: 'Edit', onClick: () => console.log('Edit event') },
                        { label: 'Delete', onClick: () => console.log('Delete event') }
                      ]}
                      onClick={() => console.log('Navigate to event')}
                    />
                  </div>
                </div>

                {/* Minimal block without arrow */}
                <div className="border rounded-lg p-6 bg-white">
                  <h4 className="text-sm font-medium mb-3">Non-clickable Block</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ListingBlock
                      title="Static Information Block"
                      subtitle="No navigation"
                      description="This block has no onClick handler, so it doesn't show an arrow icon."
                      badge={{
                        label: 'Status: Active',
                        variant: 'success'
                      }}
                      showArrow={false}
                    />
                  </div>
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Client listing with meta items and actions
<ListingBlock
  title="Acme Corporation"
  subtitle="John Smith"
  metaItems={[
    {
      icon: <Mail className="w-4 h-4" />,
      label: 'Email',
      value: 'john@acme.com'
    },
    {
      icon: <Phone className="w-4 h-4" />,
      label: 'Phone',
      value: '+1 (555) 123-4567'
    }
  ]}
  badge={{ label: 'ID: acme-corp', variant: 'secondary' }}
  actions={[
    { label: 'Edit', onClick: () => editClient(client) },
    { label: 'Delete', onClick: () => deleteClient(client) }
  ]}
  onClick={() => navigate(\`/clients/\${client.id}\`)}
/>

// Event listing with description
<ListingBlock
  title="Annual Conference 2024" 
  subtitle="/annual-conference-2024"
  description="Description text that shows up to 2 lines..."
  badge={{ label: 'ID: conf-2024', variant: 'secondary' }}
  actions={eventActions}
  onClick={() => navigate(\`/events/\${event.id}\`)}
/>

// Key features:
// ✅ Arrow icon properly positioned all the way to the right
// ✅ Responsive layout with proper text truncation
// ✅ Flexible meta items with icons
// ✅ Action buttons with event propagation handling
// ✅ Consistent spacing and typography
// ✅ Badge variants for different statuses`}
              </pre>
            </div>
          </div>
        </section>

        {/* Quick Actions Component */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Quick Actions</h3>
              <div className="space-y-6">
                {/* Standard 2-column layout */}
                <div className="border rounded-lg p-6 bg-white">
                  <QuickActions 
                    title="Client Dashboard Actions"
                    items={[
                      {
                        title: 'Events',
                        description: 'Manage events for this client',
                        icon: <CalendarIcon className="w-6 h-6" />,
                        href: '#',
                        count: 5,
                        onClick: () => console.log('Navigate to events'),
                      },
                      {
                        title: 'Invoices',
                        description: 'View and manage invoices',
                        icon: <FileText className="w-6 h-6" />,
                        href: '#',
                        comingSoon: true,
                      },
                      {
                        title: 'Transactions',
                        description: 'Payment history and records',
                        icon: <DollarSign className="w-6 h-6" />,
                        href: '#',
                        comingSoon: true,
                      },
                      {
                        title: 'Settings',
                        description: 'Client preferences and configuration',
                        icon: <Settings className="w-6 h-6" />,
                        href: '#',
                        comingSoon: true,
                      },
                    ]}
                    columns={2}
                  />
                </div>

                {/* Single column layout */}
                <div className="border rounded-lg p-6 bg-white">
                  <QuickActions 
                    title="Single Column Layout"
                    items={[
                      {
                        title: 'Attendees',
                        description: 'Manage event attendees and registrations',
                        icon: <Users className="w-6 h-6" />,
                        href: '#',
                        count: 42,
                        onClick: () => console.log('Navigate to attendees'),
                      },
                      {
                        title: 'Reports',
                        description: 'Analytics and event performance data',
                        icon: <FileText className="w-6 h-6" />,
                        href: '#',
                        comingSoon: true,
                      },
                    ]}
                    columns={1}
                  />
                </div>

                {/* Without title */}
                <div className="border rounded-lg p-6 bg-white">
                  <h4 className="text-sm font-medium mb-3">Without Title Prop</h4>
                  <QuickActions 
                    items={[
                      {
                        title: 'Quick Action',
                        description: 'This layout has no title prop',
                        icon: <CalendarIcon className="w-6 h-6" />,
                        href: '#',
                        onClick: () => console.log('Navigate somewhere'),
                      },
                    ]}
                    columns={1}
                  />
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Standard usage with counts and coming soon badges
<QuickActions 
  title="Quick Actions"
  items={[
    {
      title: 'Events',
      description: 'Manage events for this client',
      icon: <Calendar className="w-6 h-6" />,
      href: '/events',
      count: 5,
      onClick: () => navigate('/events'),
    },
    {
      title: 'Reports',
      description: 'Analytics and performance',
      icon: <FileText className="w-6 h-6" />,
      href: '#',
      comingSoon: true,
    },
  ]}
  columns={2}
/>

// Mobile-responsive features:
// - Icons vertically centered on all screen sizes
// - Count badges inline with title
// - "Coming Soon" badges below title/description on mobile
// - Actions stack on mobile, side-by-side on desktop
// - Touch-friendly spacing and tap targets`}
              </pre>
            </div>
          </div>
        </section>

        {/* Page Header Component */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Page Headers</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Responsive Page Headers</h3>
              <div className="space-y-6">
                {/* Simple header */}
                <div className="border rounded-lg p-6 bg-white">
                  <PageHeader title="Page Title" />
                </div>

                {/* Header with description */}
                <div className="border rounded-lg p-6 bg-white">
                  <PageHeader 
                    title="Page with Description" 
                    description="This is a helpful description that explains what this page is about"
                  />
                </div>

                {/* Header with single action */}
                <div className="border rounded-lg p-6 bg-white">
                  <PageHeader 
                    title="Page with Single Action" 
                    description="Mobile-first responsive layout with one primary action"
                  >
                    <Button size="sm">Primary Action</Button>
                  </PageHeader>
                </div>

                {/* Header with multiple actions */}
                <div className="border rounded-lg p-6 bg-white">
                  <PageHeader 
                    title="Page with Multiple Actions" 
                    description="Actions stack vertically on mobile, horizontally on desktop"
                  >
                    <>
                      <Button variant="ghost" size="sm">Back</Button>
                      <Button variant="outline" size="sm">Secondary</Button>
                      <Button size="sm">Primary Action</Button>
                    </>
                  </PageHeader>
                </div>

                {/* Header with long title (truncation demo) */}
                <div className="border rounded-lg p-6 bg-white">
                  <PageHeader 
                    title="Very Long Page Title That Would Overflow on Mobile Without Proper Handling" 
                    description="Title truncates with ellipsis on mobile, grows on larger screens"
                  >
                    <>
                      <Button variant="outline" size="sm">Action 1</Button>
                      <Button size="sm">Action 2</Button>
                    </>
                  </PageHeader>
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Simple header
<PageHeader title="Page Title" />

// Header with description
<PageHeader 
  title="Page with Description" 
  description="Helpful description text"
/>

// Single action (mobile-first responsive)
<PageHeader title="Page Title" description="Description">
  <Button size="sm">Primary Action</Button>
</PageHeader>

// Multiple actions (stacks on mobile, horizontal on desktop)
<PageHeader title="Page Title" description="Description">
  <>
    <Button variant="ghost" size="sm">Back</Button>
    <Button variant="outline" size="sm">Secondary</Button>
    <Button size="sm">Primary Action</Button>
  </>
</PageHeader>

// Key features:
// - Mobile-first: actions stack vertically on small screens
// - Desktop: actions display horizontally with proper spacing
// - Long titles truncate with ellipsis on mobile
// - Responsive typography scaling (text-2xl -> text-3xl)`}
              </pre>
            </div>
          </div>
        </section>

        {/* Feedback Components */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Feedback Components</h2>
          <div className="space-y-8">
            {/* Alerts */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Alerts</h3>
              <Stack spacing={4}>
                <Alert variant="info" title="Information">
                  This is an informational message.
                </Alert>
                <Alert variant="success" title="Success!">
                  Your operation completed successfully.
                </Alert>
                <Alert variant="warning" title="Warning">
                  Please review this important information.
                </Alert>
                <Alert variant="error" title="Error" onClose={() => {}}>
                  Something went wrong. Please try again.
                </Alert>
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Alert variant="info" title="Information">
  This is an informational message.
</Alert>
<Alert variant="success" title="Success!">
  Your operation completed successfully.
</Alert>
<Alert variant="error" title="Error" onClose={() => {}}>
  Something went wrong. Please try again.
</Alert>`}
              </pre>
            </div>

            {/* Toast Notifications */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Toast Notifications</h3>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => useNotificationStore.getState().success('Operation completed!')}
                >
                  Success Toast
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => useNotificationStore.getState().error('Something went wrong!')}
                >
                  Error Toast
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => useNotificationStore.getState().info('This is a detailed message')}
                >
                  Info Toast
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    useNotificationStore.getState().info('Loading...');
                    setTimeout(() => useNotificationStore.getState().success('Loaded successfully!'), 2000);
                  }}
                >
                  Promise Toast
                </Button>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { showToast } from '@/components/ui';

// Simple toast
showToast.success('Operation completed!');
showToast.error('Something went wrong!');

// Toast with title and description
showToast.info({ 
  title: 'Info', 
  description: 'This is a detailed message' 
});

// Promise toast
showToast.promise(fetchData(), {
  loading: 'Loading...',
  success: 'Loaded successfully!',
  error: 'Failed to load',
});`}
              </pre>
            </div>

            {/* Progress */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Progress Indicators</h3>
              <Stack spacing={6}>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Linear Progress</p>
                  <Stack spacing={3}>
                    <Progress value={progressValue} max={100} />
                    <Progress value={75} max={100} showLabel />
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => setProgressValue(Math.max(0, progressValue - 10))}>
                        -10
                      </Button>
                      <Button size="sm" onClick={() => setProgressValue(Math.min(100, progressValue + 10))}>
                        +10
                      </Button>
                    </div>
                  </Stack>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Circular Progress</p>
                  <div className="flex items-center gap-4">
                    <CircularProgress value={25} size="sm" />
                    <CircularProgress value={50} size="md" />
                    <CircularProgress value={75} size="lg" showLabel />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Loading Spinner</p>
                  <div className="flex items-center gap-4">
                    <Spinner size="sm" />
                    <Spinner size="md" />
                    <Spinner size="lg" />
                  </div>
                </div>
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Progress value={75} max={100} />
<Progress value={75} max={100} showLabel />

<CircularProgress value={75} size="lg" showLabel />

<Spinner size="md" />`}
              </pre>
            </div>
          </div>
        </section>

        {/* Navigation Components */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Navigation Components</h2>
          <div className="space-y-8">
            {/* Tabs */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Tabs</h3>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="disabled" disabled>Disabled</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="p-4 bg-gray-50 rounded">
                  <p>Overview content goes here. You can put any content inside tabs.</p>
                </TabsContent>
                <TabsContent value="details" className="p-4 bg-gray-50 rounded">
                  <p>Details content goes here. Each tab can have different content.</p>
                </TabsContent>
                <TabsContent value="settings" className="p-4 bg-gray-50 rounded">
                  <p>Settings content goes here. Tabs help organize related content.</p>
                </TabsContent>
              </Tabs>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Tabs value={activeTab} onValueChange={setActiveTab}>
  <TabsList>
    <TabsTrigger value="overview">Overview</TabsTrigger>
    <TabsTrigger value="details">Details</TabsTrigger>
    <TabsTrigger value="settings">Settings</TabsTrigger>
  </TabsList>
  <TabsContent value="overview">
    Overview content goes here.
  </TabsContent>
  <TabsContent value="details">
    Details content goes here.
  </TabsContent>
  <TabsContent value="settings">
    Settings content goes here.
  </TabsContent>
</Tabs>`}
              </pre>
            </div>

            {/* Breadcrumb */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Breadcrumb</h3>
              <Stack spacing={4}>
                <Breadcrumb 
                  items={[
                    { label: 'Home', href: '/dashboard' },
                    { label: 'Products', href: '/products' },
                    { label: 'Electronics', href: '/products/electronics' },
                    { label: 'Laptop', current: true },
                  ]}
                />
                <BreadcrumbWithIcons
                  items={[
                    { label: 'Home', href: '/dashboard' },
                    { label: 'Settings', href: '/settings' },
                    { label: 'Profile', current: true },
                  ]}
                />
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Breadcrumb 
  items={[
    { label: 'Home', href: '/dashboard' },
    { label: 'Products', href: '/products' },
    { label: 'Electronics', href: '/products/electronics' },
    { label: 'Laptop', current: true },
  ]}
/>

<BreadcrumbWithIcons
  items={[
    { label: 'Home', href: '/dashboard' },
    { label: 'Settings', href: '/settings' },
    { label: 'Profile', current: true },
  ]}
/>`}
              </pre>
            </div>

            {/* Pagination */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Pagination</h3>
              <Stack spacing={4}>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Standard Pagination</p>
                  <Pagination 
                    currentPage={currentPage}
                    totalPages={10}
                    onPageChange={setCurrentPage}
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Simple Pagination</p>
                  <SimplePagination
                    currentPage={currentPage}
                    totalPages={10}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Pagination 
  currentPage={currentPage}
  totalPages={10}
  onPageChange={setCurrentPage}
/>

<SimplePagination
  currentPage={currentPage}
  totalPages={10}
  onPageChange={setCurrentPage}
/>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Steps Component</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Usage</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Default horizontal steps</p>
                  <Steps 
                    steps={updateStepStatus(createSteps(['Upload', 'Process', 'Review', 'Import', 'Complete']), 2)}
                    showLabels={true}
                    size="md"
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Responsive (desktop shows full, mobile shows compact)</p>
                  <Steps 
                    steps={updateStepStatus(createSteps(['Upload File', 'AI Processing', 'Review Data', 'Import to Database', 'Complete']), 1)}
                    responsive={true}
                    showLabels={true}
                    size="md"
                  />
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { Steps, createSteps, updateStepStatus } from '@/components/ui';

const steps = updateStepStatus(
  createSteps(['Upload', 'Process', 'Review', 'Import', 'Complete']), 
  2 // Current step index (0-based)
);

<Steps 
  steps={steps}
  responsive={true}
  showLabels={true}
  size="md"
/>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Sizes</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Small</p>
                  <Steps 
                    steps={updateStepStatus(createSteps(['Step 1', 'Step 2', 'Step 3']), 1)}
                    size="sm"
                    showLabels={true}
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Medium (default)</p>
                  <Steps 
                    steps={updateStepStatus(createSteps(['Step 1', 'Step 2', 'Step 3']), 1)}
                    size="md"
                    showLabels={true}
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Large</p>
                  <Steps 
                    steps={updateStepStatus(createSteps(['Step 1', 'Step 2', 'Step 3']), 1)}
                    size="lg"
                    showLabels={true}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Vertical Orientation</h3>
              <div className="max-w-xs">
                <Steps 
                  steps={updateStepStatus(createSteps(['Create Account', 'Verify Email', 'Setup Profile', 'Complete']), 2)}
                  orientation="vertical"
                  showLabels={true}
                  showDescriptions={false}
                  size="md"
                />
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Steps 
  steps={steps}
  orientation="vertical"
  showLabels={true}
  size="md"
/>`}
              </pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Interactive Steps</h3>
              <Steps 
                steps={updateStepStatus(createSteps(['Configure', 'Deploy', 'Test', 'Launch']), 1)}
                showLabels={true}
                size="md"
                onStepClick={(step, index) => {
                  notifySuccess(`Clicked step ${index + 1}: ${step.label}`);
                }}
              />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Steps 
  steps={steps}
  showLabels={true}
  onStepClick={(step, index) => {
    // Handle step click
    console.log('Clicked step:', index + 1, step.label);
  }}
/>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Location Autocomplete */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Location Autocomplete</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Google Places Integration</h3>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Address and city search</p>
                  <LocationAutocomplete
                    placeholder="Search for any location..."
                    types="address"
                    onLocationSelect={(location) => {
                      notifySuccess(`Selected: ${location.formatted_address}`);
                      console.log('Location data:', location);
                    }}
                  />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Cities only</p>
                  <LocationAutocomplete
                    placeholder="Search for cities..."
                    types="cities"
                    onLocationSelect={(location) => {
                      notifySuccess(`Selected: ${location.city}, ${location.region}, ${location.country_iso2}`);
                    }}
                  />
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { LocationAutocomplete } from '@/components/ui';

<LocationAutocomplete
  placeholder="Search for locations..."
  types="address" // or "cities"
  onLocationSelect={(location) => {
    console.log('Selected location:', location);
    // location contains: place_id, lat, lon, city, region, country_iso2, etc.
  }}
/>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Calendar Kit */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Calendar (Content Plan)</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Interactive Calendar</h3>
              <Calendar events={demoEvents} />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { Calendar } from '@/components/ui/Calendar';

const events = [
  { id: '1', title: 'Instagram Carousel: Workshop Teaser', start: new Date(), end: new Date(), networks: ['instagram'], status: 'scheduled', colorVariant: 'primary' },
];

<Calendar events={events} initialView="month" />`}
              </pre>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Month View</h3>
              <MonthView events={demoEvents} onEventClick={() => {/* TODO: Handle event click */}} />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { MonthView } from '@/components/ui/Calendar';

<MonthView month={new Date()} events={events} onEventClick={handleClick} />`}
              </pre>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Week View</h3>
              <WeekView events={demoEvents} onEventClick={() => {/* TODO: Handle event click */}} />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { WeekView } from '@/components/ui/Calendar';

<WeekView weekStart={new Date()} events={events} onEventClick={handleClick} />`}
              </pre>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Day View</h3>
              <DayView events={demoEvents} onEventClick={() => {/* TODO: Handle event click */}} />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { DayView } from '@/components/ui/Calendar';

<DayView day={new Date()} events={events} onEventClick={handleClick} />`}
              </pre>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Agenda View</h3>
              <AgendaView events={demoEvents} onEventClick={() => {/* TODO: Handle event click */}} />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`import { AgendaView } from '@/components/ui/Calendar';

const start = new Date();
const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);

<AgendaView from={start} to={end} events={events} onEventClick={handleClick} />`}
              </pre>
            </div>
          </div>
        </section>

        {/* Overlays */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Overlays</h2>
          <div className="space-y-8">
            {/* Modal & Dialog */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Modal & Dialog</h3>
              <div className="flex gap-3">
                <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
                <Button variant="danger" onClick={() => setDialogOpen(true)}>Open Dialog</Button>
              </div>

              <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Modal Title"
                description="This is a modal description that provides context."
              >
                <div className="space-y-4">
                  <p>Modal content goes here. You can put any content inside.</p>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={() => setModalOpen(false)}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              </Modal>

              <Dialog
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onConfirm={() => {
                  // TODO: Handle confirmation
                  setDialogOpen(false);
                }}
                title="Confirm Action"
                description="Are you sure you want to proceed? This action cannot be undone."
                confirmText="Delete"
                confirmVariant="danger"
              />
              
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Modal
<Modal
  isOpen={modalOpen}
  onClose={() => setModalOpen(false)}
  title="Modal Title"
  description="This is a modal description."
>
  <div>Modal content goes here.</div>
</Modal>

// Confirmation Dialog
<Dialog
  isOpen={dialogOpen}
  onClose={() => setDialogOpen(false)}
  onConfirm={() => handleDelete()}
  title="Confirm Action"
  description="Are you sure you want to proceed?"
  confirmText="Delete"
  confirmVariant="danger"
/>`}
              </pre>
            </div>

            {/* Dropdown & Tooltip */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Dropdown & Tooltip</h3>
              <div className="flex items-center gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">Options</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => {/* TODO: Handle edit */}}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {/* TODO: Handle duplicate */}}>
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {/* TODO: Handle share */}}>
                      Share
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => {/* TODO: Handle delete */}} className="text-destructive">
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <SimpleTooltip content="This is a helpful tooltip">
                  <Button variant="ghost" size="icon">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Button>
                </SimpleTooltip>

                <SimpleTooltip content="Another tooltip example">
                  <span className="text-muted-foreground cursor-help">Hover me</span>
                </SimpleTooltip>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`// Dropdown Menu
<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button variant="outline">Options</Button>
  </DropdownMenuTrigger>
  <DropdownMenuContent>
    <DropdownMenuItem onClick={() => {/* TODO: Handle edit */}}>
      Edit
    </DropdownMenuItem>
    <DropdownMenuItem onClick={() => {/* TODO: Handle share */}}>
      Share
    </DropdownMenuItem>
    <DropdownMenuSeparator />
    <DropdownMenuItem onClick={() => {/* TODO: Handle delete */}}>
      Delete
    </DropdownMenuItem>
  </DropdownMenuContent>
</DropdownMenu>

// Tooltip
<SimpleTooltip content="This is a helpful tooltip">
  <Button variant="ghost" size="icon">?</Button>
</SimpleTooltip>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Data Display Components */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Data Display Components</h2>
          <div className="space-y-8">
            {/* Table */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Table</h3>
              <Table
                data={[
                  { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'Active', created: '2024-01-15' },
                  { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Member', status: 'Active', created: '2024-01-10' },
                  { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'Member', status: 'Inactive', created: '2024-01-05' },
                  { id: 4, name: 'Alice Brown', email: 'alice@example.com', role: 'Admin', status: 'Active', created: '2024-01-01' },
                  { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', role: 'Member', status: 'Active', created: '2023-12-28' },
                ]}
                columns={[
                  { key: 'name', title: 'Name', sortable: true },
                  { key: 'email', title: 'Email', sortable: true },
                  { 
                    key: 'role', 
                    title: 'Role', 
                    sortable: true,
                    render: (value) => <Badge variant={value === 'Admin' ? 'secondary' : 'outline'}>{value}</Badge>
                  },
                  { 
                    key: 'status', 
                    title: 'Status', 
                    sortable: true,
                    render: (value) => <Badge variant={value === 'Active' ? 'success' : 'warning'}>{value}</Badge>
                  },
                  { key: 'created', title: 'Created', sortable: true },
                  {
                    key: 'actions',
                    title: 'Actions',
                    align: 'right' as const,
                    render: () => (
                      <TableActions>
                        <TableActionButton icon={FilePen}>Edit</TableActionButton>
                        <TableActionButton variant="destructive" icon={Archive}>Archive</TableActionButton>
                      </TableActions>
                    )
                  }
                ]}
                filters={[
                  {
                    key: 'role',
                    label: 'Filter by Role',
                    options: [
                      { label: 'Admin', value: 'Admin' },
                      { label: 'Member', value: 'Member' },
                    ]
                  },
                  {
                    key: 'status',
                    label: 'Filter by Status',
                    options: [
                      { label: 'Active', value: 'Active' },
                      { label: 'Inactive', value: 'Inactive' },
                    ]
                  }
                ]}
                pagination={{ pageSize: 3, showSizeChanger: true, pageSizeOptions: [3, 5, 10] }}
              />
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Table
  data={users}
  columns={[
    { key: 'name', title: 'Name', sortable: true },
    { key: 'email', title: 'Email', sortable: true },
    { 
      key: 'role', 
      title: 'Role',
      render: (value) => <Badge>{value}</Badge>
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'right',
      render: () => (
        <TableActions>
          <TableActionButton icon={FilePen}>Edit</TableActionButton>
          <TableActionButton variant="destructive" icon={Archive}>Archive</TableActionButton>
        </TableActions>
      )
    }
  ]}
  filters={[
    {
      key: 'role',
      label: 'Filter by Role',
      options: [
        { label: 'Admin', value: 'Admin' },
        { label: 'Member', value: 'Member' },
      ]
    }
  ]}
  pagination={{ 
    pageSize: 10, 
    showSizeChanger: true 
  }}
/>`}
              </pre>
            </div>
          </div>
        </section>

        {/* Layout Components */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Layout Components</h2>
          <div className="space-y-8">
            {/* Container */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Container</h3>
              <Stack spacing={4}>
                <Container size="sm" className="bg-gray-100 p-4 rounded">
                  <p className="text-center">Small Container (max-width: 640px)</p>
                </Container>
                <Container size="md" className="bg-gray-100 p-4 rounded">
                  <p className="text-center">Medium Container (max-width: 768px)</p>
                </Container>
                <Container size="lg" className="bg-gray-100 p-4 rounded">
                  <p className="text-center">Large Container (max-width: 1024px)</p>
                </Container>
              </Stack>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Container size="sm">
  <p>Small Container content</p>
</Container>

<Container size="lg" className="custom-class">
  <p>Large Container with custom styling</p>
</Container>`}
              </pre>
            </div>

            {/* Stack */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Stack Components</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Vertical Stack</p>
                  <VStack spacing={3} className="bg-gray-100 p-4 rounded">
                    <div className="bg-white p-2 rounded text-center">Item 1</div>
                    <div className="bg-white p-2 rounded text-center">Item 2</div>
                    <div className="bg-white p-2 rounded text-center">Item 3</div>
                  </VStack>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Horizontal Stack</p>
                  <HStack spacing={3} className="bg-gray-100 p-4 rounded">
                    <div className="bg-white p-2 rounded">Item 1</div>
                    <div className="bg-white p-2 rounded">Item 2</div>
                    <div className="bg-white p-2 rounded">Item 3</div>
                  </HStack>
                </div>
              </div>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<VStack spacing={3}>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</VStack>

<HStack spacing={3} align="center">
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</HStack>`}
              </pre>
            </div>

            {/* Grid */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Grid Layout</h3>
              <Grid cols={3} gap={4}>
                <GridItem className="bg-gray-100 p-4 rounded text-center">Item 1</GridItem>
                <GridItem className="bg-gray-100 p-4 rounded text-center">Item 2</GridItem>
                <GridItem className="bg-gray-100 p-4 rounded text-center">Item 3</GridItem>
                <GridItem colSpan={2} className="bg-gray-100 p-4 rounded text-center">
                  Item 4 (spans 2 columns)
                </GridItem>
                <GridItem className="bg-gray-100 p-4 rounded text-center">Item 5</GridItem>
                <GridItem colSpan={3} className="bg-gray-100 p-4 rounded text-center">
                  Item 6 (spans 3 columns)
                </GridItem>
              </Grid>
              <pre className="mt-4 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`<Grid cols={3} gap={4}>
  <GridItem>Item 1</GridItem>
  <GridItem>Item 2</GridItem>
  <GridItem>Item 3</GridItem>
  <GridItem colSpan={2}>
    Item 4 (spans 2 columns)
  </GridItem>
  <GridItem>Item 5</GridItem>
  <GridItem colSpan={3}>
    Item 6 (spans 3 columns)
  </GridItem>
</Grid>`}
              </pre>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}